import { useEffect, useState } from "react"
import { isAddress } from "@ethersproject/address"
import type { BigNumberish } from "@ethersproject/bignumber"
import type { FetchSignerResult, Signer } from "@wagmi/core"
import { useQueryClient } from "@tanstack/react-query"

import { useAddress } from "web3/hooks/useAddress"
import {
  GovernorType,
  useCreateVoteAttemptMutation,
  VoteType,
} from "query/graphql"
import type { AccountId, Governor } from "query/graphql"
import type { UseCorrectChainErrors } from "web3/helpers/chain"
import { getCorrectChain } from "web3/helpers/chain"
import { getChainIdParams } from "web3/helpers/chainId"
import { getChainReferenceName } from "web3/helpers/chainReference"
import { getGovernorOpenZeppelin } from "web3/hooks/useGovernorOpenZeppelin"
import { getGovernorAlpha } from "web3/hooks/useGovernorAlpha"
import { getGovernorBravo } from "web3/hooks/useGovernorBravo"
import { getGovernorAave } from "web3/hooks/useGovernorAave"
import { getAccountIdParams, isValidAccountId } from "web3/helpers/accountId"
import { SUPPORT_NUMBERS } from "voting/constants/support"
import { useTransaction } from "web3/providers/TransactionProvider"
import type {
  TransactionOnMining,
  TransactionOnSuccess,
  TransactionStatus,
} from "web3/types/transaction"
import { TransactionState, TransactionToastState } from "web3/types/transaction"
import { useToast } from "common/hooks/useToast"
import { useModals } from "common/hooks/useModals"
import { useSafeTransactionV2 } from "safe/hooks/useSafeTransactionV2"
import { useMe } from "user/providers/MeProvider"
import { getGovernorHub } from "web3/hooks/useGovernorHub"
import { getSpokeVoteAggregator } from "web3/hooks/useSpokeVoteAggregator"
import { isSameAddress } from "web3/helpers/address"
import { useMultichainAccount } from "web3/hooks/useMultichainAccount"

type Values = {
  error: Error | undefined
  status: TransactionStatus<Metadata>
  governorId: string | null
  castVote: (args: {
    reason?: string
    signer?: FetchSignerResult<Signer>
    type: VoteType
    governance: Governor
    proposalId: BigNumberish
    onchainId: BigNumberish
  }) => Promise<void>
}

export enum UseCastVoteError {
  Signernotfound = "SIGNER_NOT_FOUND",
  Nonmatchingchain = "NON_MATCHING_CHAIN",
  Undefinedgovernance = "UNDEFINED_GOVERNANCE",
  Invalidgovernanceid = "INVALID_GOVERNANCE_ID",
  Invalidgovernoraddress = "INVALID_GOVERNOR_ADDRESS",
}

type Metadata = {
  reason?: string
  type: VoteType
  proposalId: BigNumberish
  governanceId: AccountId
  governorAddress: string
}

type Error = UseCorrectChainErrors | UseCastVoteError

type Props = {
  chainReference?: number
  isMultiChain?: boolean
}

export const useCastVote = ({
  chainReference,
  isMultiChain = false,
}: Props): Values => {
  const [error, setError] = useState<Error | undefined>(undefined)
  const [transactionState, setTransactionState] = useState<
    TransactionStatus<Metadata>
  >({
    state: TransactionState.None,
  })
  const [governorId, setGovernorId] = useState<string | null>(null)

  const me = useMe()
  const address = useAddress()
  const { multichainAccount: walletAccount } = useMultichainAccount()
  const canUseSafeTx =
    me?.type === "SAFE" && !isSameAddress(me.address, walletAccount.address)

  const { toast } = useToast()
  const { voteModal } = useModals()
  const queryClient = useQueryClient()

  const onSuccess: TransactionOnSuccess<Metadata> = () => {
    setGovernorId(null)
  }

  const onMining: TransactionOnMining<Metadata> = ({
    transaction,
    metadata,
  }) => {
    if (address) {
      const { hash } = transaction
      const { type, proposalId } = metadata

      if (!isMultiChain) {
        voteModal.onClose()
      }

      if (proposalId && hash && address && type) {
        createVoteAttempt({
          input: {
            proposalId: proposalId as string,
            txHash: hash,
            voter: address,
            type,
          },
        })
      }
    }
  }

  const handlePendingSafeTx = (values: Record<string, any>) => {
    if (
      values &&
      values.proposalId &&
      values.safeTxHash &&
      values.safe &&
      values.type
    ) {
      createVoteAttempt({
        input: {
          proposalId: values.proposalId,
          txHash: values.safeTxHash,
          voter: values.safe,
          type: values.type,
        },
      })
    }

    if (!isMultiChain) {
      voteModal.onClose()
    }
  }

  const options = {
    on: {
      [TransactionToastState.Mining]: onMining,
      [TransactionToastState.Success]: onSuccess,
    },
  }

  const {
    send: sendCastVote,
    status: castVoteStatus,
    setMetadata: setMetadataCastVote,
  } = useTransaction<Metadata>(options)
  const {
    send: sendCastVoteWithReason,
    status: castVoteWithReasonStatus,
    setMetadata: setMetadataCastVoteWithReason,
  } = useTransaction<Metadata>(options)

  const { mutate: createVoteAttempt } = useCreateVoteAttemptMutation({
    onSuccess: (data) => {
      if (data) {
        const queriesToInvalidate = ["ProposalVoteAttempt"]
        queriesToInvalidate.forEach((query) =>
          queryClient.invalidateQueries({
            queryKey: [query],
          }),
        )
      }
    },
  })

  const {
    proposeMultiGnosisTransaction,
    buildSafeTransaction,
    onPendingSafeTx,
  } = useSafeTransactionV2(
    {
      safeId: me?.id as string,
      accountAddress: me?.address,
    },
    { enabled: canUseSafeTx },
  )

  useEffect(() => {
    if (!castVoteStatus) return

    setTransactionState(castVoteStatus)
  }, [castVoteStatus])

  useEffect(() => {
    if (!castVoteWithReasonStatus) return

    setTransactionState(castVoteWithReasonStatus)
  }, [castVoteWithReasonStatus])

  const getVote = (type: VoteType) => SUPPORT_NUMBERS[type]

  const castVote = async ({
    reason,
    signer,
    type,
    governance,
    proposalId,
    onchainId,
  }: {
    reason?: string
    signer?: FetchSignerResult<Signer>
    type: VoteType
    governance: Governor
    proposalId: BigNumberish
    onchainId: BigNumberish
  }): Promise<void> => {
    if (!governance) {
      setError(UseCastVoteError.Undefinedgovernance)

      return
    }

    const { contracts, id: governanceId } = governance
    const { governor } = contracts
    const { type: governorType, address: governorAddress } = governor

    if (!isValidAccountId(governanceId)) {
      setError(UseCastVoteError.Invalidgovernanceid)

      return
    }

    if (!isAddress(governorAddress)) {
      setError(UseCastVoteError.Invalidgovernoraddress)

      return
    }

    const { chainId } = getAccountIdParams(governanceId)
    const { reference } = getChainIdParams(chainId)
    const chainReferenceName = getChainReferenceName(reference)

    if (!signer) {
      setError(UseCastVoteError.Signernotfound)
      toast({
        title: "No signer found",
        status: "error",
        description: "You should have your wallet connected",
      })

      return
    }

    const { isCorrectChain, error: useCorrectChainErrors } = getCorrectChain({
      chainId,
      chainReference,
    })

    if (!isCorrectChain) {
      setError(UseCastVoteError.Nonmatchingchain)

      toast({
        status: "warning",
        title: "Incorrect network",
        description: `You must be connected to the ${chainReferenceName} network to delegate on this governace`,
        duration: 5000,
      })

      return
    }

    if (useCorrectChainErrors !== undefined) {
      setError(useCorrectChainErrors)

      throw new Error(
        `useCastVote => "chainId" or "chainReference" or both, are incorrect.
         This should not be happening as they are being gathered from the server and the provider`,
      )
    }

    setGovernorId(governanceId)

    const governorOz = getGovernorOpenZeppelin(governorAddress, signer)
    const governorAlpha = getGovernorAlpha(governorAddress, signer)
    const governorBravo = getGovernorBravo(governorAddress, signer)
    const governorAave = getGovernorAave(governorAddress, signer)
    const governorHub = getGovernorHub(governorAddress, signer)
    const spokeVoteAggregator = getSpokeVoteAggregator(governorAddress, signer)

    switch (governorType) {
      case GovernorType.Governoralpha:
        const alphaVote = type === VoteType.For

        setMetadataCastVote({
          type,
          reason,
          proposalId,
          governanceId,
          governorAddress,
        })

        if (canUseSafeTx) {
          try {
            const safeTxHash = await proposeMultiGnosisTransaction([
              buildSafeTransaction(
                governorAlpha,
                "castVote",
                [onchainId, alphaVote],
                governorAddress,
              ),
            ])

            onPendingSafeTx(safeTxHash, governanceId, {
              requiredParameters: ["proposalId", "alphaVote"],
              expectedMethod: "castVote",
            })((values) =>
              handlePendingSafeTx({
                ...values,
                type,
                proposalId,
              }),
            )
          } catch (error: any) {
            if (error && error.message.includes("user rejected signing")) {
              toast({
                status: "error",
                title: "User rejected signature",
                duration: 5000,
              })
            }
          }
        } else {
          sendCastVote(() => governorAlpha.castVote(onchainId, alphaVote))
        }
        break

      case GovernorType.Nounsfork:
      case GovernorType.Governorbravo:
        const bravoVote = getVote(type)

        if (!reason) {
          setMetadataCastVote({
            type,
            reason,
            proposalId,
            governanceId,
            governorAddress,
          })

          if (canUseSafeTx) {
            try {
              const safeTxHash = await proposeMultiGnosisTransaction([
                buildSafeTransaction(
                  governorBravo,
                  "castVote",
                  [onchainId, bravoVote],
                  governorAddress,
                ),
              ])

              onPendingSafeTx(safeTxHash, governanceId, {
                requiredParameters: ["proposalId", "bravoVote"],
                expectedMethod: "castVote",
              })((values) =>
                handlePendingSafeTx({
                  ...values,
                  type,
                  proposalId,
                }),
              )
            } catch (error: any) {
              if (error && error.message.includes("user rejected signing")) {
                toast({
                  status: "error",
                  title: "User rejected signature",
                  duration: 5000,
                })
              }
            }
          } else {
            sendCastVote(() => governorBravo.castVote(onchainId, bravoVote))
          }
          break
        }

        setMetadataCastVoteWithReason({
          type,
          reason,
          proposalId,
          governanceId,
          governorAddress,
        })

        if (canUseSafeTx) {
          try {
            const safeTxHash = await proposeMultiGnosisTransaction([
              buildSafeTransaction(
                governorBravo,
                "castVoteWithReason",
                [onchainId, bravoVote, reason],
                governorAddress,
              ),
            ])

            onPendingSafeTx(safeTxHash, governanceId, {
              requiredParameters: ["proposalId", "bravoVote", "reason"],
              expectedMethod: "castVoteWithReason",
            })((values) => handlePendingSafeTx({ ...values, type, proposalId }))
          } catch (error: any) {
            if (error && error.message.includes("user rejected signing")) {
              toast({
                status: "error",
                title: "User rejected signature",
                duration: 5000,
              })
            }
          }
        } else {
          sendCastVoteWithReason(() =>
            governorBravo.castVoteWithReason(onchainId, bravoVote, reason),
          )
        }

        break

      case GovernorType.Openzeppelingovernor:
        const ozVote = getVote(type)

        if (!reason) {
          setMetadataCastVote({
            type,
            reason,
            proposalId,
            governanceId,
            governorAddress,
          })

          if (canUseSafeTx) {
            try {
              const safeTxHash = await proposeMultiGnosisTransaction([
                buildSafeTransaction(
                  governorOz,
                  "castVote",
                  [onchainId, ozVote],
                  governorAddress,
                ),
              ])

              if (safeTxHash) {
                setTransactionState({
                  state: TransactionState.PendingSafe,
                  metadata: {
                    type,
                    reason,
                    proposalId,
                    governanceId,
                    governorAddress,
                  },
                  safeTxHash: safeTxHash,
                })
              }

              onPendingSafeTx(safeTxHash, governanceId, {
                requiredParameters: ["proposalId", "support"],
                expectedMethod: "castVote",
              })((values) => {
                handlePendingSafeTx({ ...values, type, proposalId })
              })
            } catch (error: any) {
              if (error && error.message.includes("user rejected signing")) {
                toast({
                  status: "error",
                  title: "User rejected signature",
                  duration: 5000,
                })
              }
            }
          } else {
            sendCastVote(() => governorOz.castVote(onchainId, ozVote))
          }

          break
        }

        setMetadataCastVoteWithReason({
          type,
          reason,
          proposalId,
          governanceId,
          governorAddress,
        })

        if (canUseSafeTx) {
          try {
            const safeTxHash = await proposeMultiGnosisTransaction([
              buildSafeTransaction(
                governorOz,
                "castVoteWithReason",
                [onchainId, ozVote, reason],
                governorAddress,
              ),
            ])

            onPendingSafeTx(safeTxHash, governanceId, {
              requiredParameters: ["proposalId", "support", "ozVote"],
              expectedMethod: "castVoteWithReason",
            })((values) => handlePendingSafeTx({ ...values, type, proposalId }))
          } catch (error: any) {
            if (error && error.message.includes("user rejected signing")) {
              toast({
                status: "error",
                title: "User rejected signature",
                duration: 5000,
              })
            }
          }
        } else {
          sendCastVoteWithReason(() =>
            governorOz.castVoteWithReason(onchainId, ozVote, reason),
          )
        }

        break

      case GovernorType.Aave:
        const vote = type === VoteType.For

        setMetadataCastVote({
          type,
          proposalId,
          governanceId,
          governorAddress,
        })

        if (canUseSafeTx) {
          try {
            const safeTxHash = await proposeMultiGnosisTransaction([
              buildSafeTransaction(
                governorAave,
                "submitVote",
                [onchainId, vote],
                governorAddress,
              ),
            ])

            onPendingSafeTx(safeTxHash, governanceId, {
              requiredParameters: ["proposalId", "vote"],
              expectedMethod: "submitVote",
            })((values) => handlePendingSafeTx({ ...values, type, proposalId }))
          } catch (error: any) {
            if (error && error.message.includes("user rejected signing")) {
              toast({
                status: "error",
                title: "User rejected signature",
                duration: 5000,
              })
            }
          }
        } else {
          sendCastVote(() => governorAave.submitVote(onchainId, vote))
        }

        break

      case GovernorType.Hub:
        const hubVote = getVote(type)

        if (!reason) {
          setMetadataCastVote({
            type,
            reason,
            proposalId,
            governanceId,
            governorAddress,
          })

          if (canUseSafeTx) {
            try {
              const safeTxHash = await proposeMultiGnosisTransaction([
                buildSafeTransaction(
                  governorHub,
                  "castVote",
                  [onchainId, hubVote],
                  governorAddress,
                ),
              ])

              if (safeTxHash) {
                setTransactionState({
                  state: TransactionState.PendingSafe,
                  metadata: {
                    type,
                    reason,
                    proposalId,
                    governanceId,
                    governorAddress,
                  },
                  safeTxHash: safeTxHash,
                })
              }

              onPendingSafeTx(safeTxHash, governanceId, {
                requiredParameters: ["proposalId", "support"],
                expectedMethod: "castVote",
              })((values) => {
                handlePendingSafeTx({ ...values, type, proposalId })
              })
            } catch (error: any) {
              if (error && error.message.includes("user rejected signing")) {
                toast({
                  status: "error",
                  title: "User rejected signature",
                  duration: 5000,
                })
              }
            }
          } else {
            sendCastVote(() => governorHub.castVote(onchainId, hubVote))
          }

          break
        }

        setMetadataCastVoteWithReason({
          type,
          reason,
          proposalId,
          governanceId,
          governorAddress,
        })

        if (canUseSafeTx) {
          try {
            const safeTxHash = await proposeMultiGnosisTransaction([
              buildSafeTransaction(
                governorHub,
                "castVoteWithReason",
                [onchainId, hubVote, reason],
                governorAddress,
              ),
            ])

            onPendingSafeTx(safeTxHash, governanceId, {
              requiredParameters: ["proposalId", "support", "ozVote"],
              expectedMethod: "castVoteWithReason",
            })((values) => handlePendingSafeTx({ ...values, type, proposalId }))
          } catch (error: any) {
            if (error && error.message.includes("user rejected signing")) {
              toast({
                status: "error",
                title: "User rejected signature",
                duration: 5000,
              })
            }
          }
        } else {
          sendCastVoteWithReason(() =>
            governorHub.castVoteWithReason(onchainId, hubVote, reason),
          )
        }

        break

      case GovernorType.Spoke:
        const spokeVote = getVote(type)

        if (!reason) {
          setMetadataCastVote({
            type,
            reason,
            proposalId,
            governanceId,
            governorAddress,
          })

          if (canUseSafeTx) {
            try {
              const safeTxHash = await proposeMultiGnosisTransaction([
                buildSafeTransaction(
                  spokeVoteAggregator,
                  "castVote",
                  [onchainId, spokeVote],
                  governorAddress,
                ),
              ])

              if (safeTxHash) {
                setTransactionState({
                  state: TransactionState.PendingSafe,
                  metadata: {
                    type,
                    reason,
                    proposalId,
                    governanceId,
                    governorAddress,
                  },
                  safeTxHash: safeTxHash,
                })
              }

              onPendingSafeTx(safeTxHash, governanceId, {
                requiredParameters: ["proposalId", "support"],
                expectedMethod: "castVote",
              })((values) => {
                handlePendingSafeTx({ ...values, type, proposalId })
              })
            } catch (error: any) {
              if (error && error.message.includes("user rejected signing")) {
                toast({
                  status: "error",
                  title: "User rejected signature",
                  duration: 5000,
                })
              }
            }
          } else {
            sendCastVote(() =>
              spokeVoteAggregator.castVote(onchainId, spokeVote),
            )
          }

          break
        }

        setMetadataCastVoteWithReason({
          type,
          reason,
          proposalId,
          governanceId,
          governorAddress,
        })

        if (canUseSafeTx) {
          try {
            const safeTxHash = await proposeMultiGnosisTransaction([
              buildSafeTransaction(
                spokeVoteAggregator,
                "castVoteWithReason",
                [onchainId, spokeVote, reason],
                governorAddress,
              ),
            ])

            onPendingSafeTx(safeTxHash, governanceId, {
              requiredParameters: ["proposalId", "support", "ozVote"],
              expectedMethod: "castVoteWithReason",
            })((values) => handlePendingSafeTx({ ...values, type, proposalId }))
          } catch (error: any) {
            if (error && error.message.includes("user rejected signing")) {
              toast({
                status: "error",
                title: "User rejected signature",
                duration: 5000,
              })
            }
          }
        } else {
          sendCastVoteWithReason(() =>
            spokeVoteAggregator.castVoteWithReason(
              onchainId,
              spokeVote,
              reason,
            ),
          )
        }

        break

      default:
        break
    }
  }

  return {
    error,
    status: transactionState,
    governorId,
    castVote,
  }
}
